/**
 * Grouped Variant Selector
 * Handles grouped teat flow variant selection and ensures only one option can be selected at a time
 */
class GroupedVariantSelector {
  constructor() {
    this.groupedVariants = document.querySelectorAll('[data-grouped-variants]');
    this.init();
  }

  init() {
    if (this.groupedVariants.length === 0) return;

    this.groupedVariants.forEach(container => {
      this.setupGroupedSelection(container);
    });

    // Setup category tooltips
    this.setupCategoryTooltips();

    // Listen for external variant changes (from URL, other selectors, etc.)
    document.addEventListener('variant:change', (event) => {
      this.handleExternalVariantChange(event.detail.variant);
    });
  }

  setupGroupedSelection(container) {
    const inputs = container.querySelectorAll('[data-grouped-variant-input]');
    
    inputs.forEach(input => {
      input.addEventListener('change', (event) => {
        this.handleGroupedVariantChange(event.target, container);
      });
    });
  }

  handleGroupedVariantChange(selectedInput, container) {
    if (!selectedInput.checked) return;

    // Uncheck all other inputs in the grouped variants container
    const allInputs = container.querySelectorAll('[data-grouped-variant-input]');
    allInputs.forEach(input => {
      if (input !== selectedInput) {
        input.checked = false;
      }
    });

    // Update the displayed value in the legend
    const legend = container.closest('.selector-wrapper').querySelector('[data-option-value]');
    if (legend) {
      legend.textContent = selectedInput.value;
    }

    // Trigger variant change event for the product form
    this.triggerVariantUpdate();
  }

  handleExternalVariantChange(variant) {
    if (!variant) return;

    // Update grouped variant inputs to match the selected variant
    this.groupedVariants.forEach(container => {
      const inputs = container.querySelectorAll('[data-grouped-variant-input]');
      const optionPosition = container.closest('[data-option-position]')?.dataset.optionPosition;
      
      if (optionPosition && variant.options) {
        const variantValue = variant.options[parseInt(optionPosition) - 1];
        
        inputs.forEach(input => {
          input.checked = (input.value === variantValue);
        });

        // Update the displayed value
        const legend = container.closest('.selector-wrapper').querySelector('[data-option-value]');
        if (legend && variantValue) {
          legend.textContent = variantValue;
        }
      }
    });
  }

  triggerVariantUpdate() {
    // Find the product form and trigger its variant update
    const productForm = document.querySelector('[data-product-form]');
    if (!productForm) return;

    // Get all option inputs
    const allOptionInputs = productForm.querySelectorAll('input[name^="options["], select[name^="options["]');
    const options = [];

    allOptionInputs.forEach(input => {
      if (input.type === 'radio' && input.checked) {
        options.push(input.value);
      } else if (input.type === 'hidden' || input.tagName === 'SELECT') {
        options.push(input.value);
      }
    });

    // Find the matching variant
    const product = this.getProductData();
    if (!product) return;

    const matchingVariant = product.variants.find(variant => {
      return variant.options.every((option, index) => option === options[index]);
    });

    if (matchingVariant) {
      // Update the variant ID input
      const variantIdInput = productForm.querySelector('input[name="id"]');
      if (variantIdInput) {
        variantIdInput.value = matchingVariant.id;
      }

      // Dispatch variant change event
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: {
          variant: matchingVariant,
          options: options
        },
        bubbles: true
      }));
    }
  }

  setupCategoryTooltips() {
    const tooltipTriggers = document.querySelectorAll('.category-tooltip-trigger');
    
    tooltipTriggers.forEach(trigger => {
      // Mobile tap interaction
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const tooltip = trigger.nextElementSibling;
        const isShowing = tooltip.classList.contains('show');
        
        // Hide all other category tooltips
        document.querySelectorAll('.category-tooltip-content.show')
          .forEach(t => t.classList.remove('show'));
        
        // Toggle current tooltip
        if (!isShowing) {
          tooltip.classList.add('show');
        }
      });
    });
    
    // Hide tooltips when clicking elsewhere
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.category-tooltip-trigger')) {
        document.querySelectorAll('.category-tooltip-content.show')
          .forEach(t => t.classList.remove('show'));
      }
    });
  }

  getProductData() {
    // Try to get product data from various sources
    let productData = null;

    // Method 1: From global theme object
    if (window.theme && window.theme.product) {
      productData = window.theme.product;
    }

    // Method 2: From product JSON script tag
    if (!productData) {
      const productScript = document.querySelector('[data-product-json]');
      if (productScript) {
        try {
          productData = JSON.parse(productScript.textContent);
        } catch (e) {
          console.warn('Could not parse product JSON:', e);
        }
      }
    }

    // Method 3: From product form data attribute
    if (!productData) {
      const productForm = document.querySelector('[data-product-form]');
      if (productForm && productForm.dataset.product) {
        try {
          productData = JSON.parse(productForm.dataset.product);
        } catch (e) {
          console.warn('Could not parse product form data:', e);
        }
      }
    }

    return productData;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  new GroupedVariantSelector();
});

// Re-initialize on AJAX page loads (for themes with AJAX navigation)
document.addEventListener('shopify:section:load', function() {
  new GroupedVariantSelector();
});

// Export for potential use by other scripts
window.GroupedVariantSelector = GroupedVariantSelector;